import { useAuth } from "src/hooks/useAuth";
import { useEffect, useReducer } from "react";
import type { QueryResult } from "src/types/adapters/query.type";
import { createReducer, type ReducerAction } from "src/utils/create-reducer";
import { useShopTypeContext } from "../context/use-shop-type-context";
import { useSearchProvider } from "../use-search-provider";

type SearchState = {
  loading: boolean;
  results: QueryResult | undefined;
  totalResults: number;
  error: boolean;
};

type SearchActions =
  | ReducerAction<"SET_RESULT", QueryResult>
  | ReducerAction<"SET_LOADING", boolean>
  | ReducerAction<"SET_ERROR", boolean>;

const searchReducer = createReducer<SearchState, SearchActions>({
  SET_LOADING(state, payload) {
    return { ...state, loading: payload };
  },
  SET_RESULT(state, payload) {
    return { ...state, results: payload, loading: false };
  },
  SET_ERROR(state, payload) {
    return { ...state, error: payload, loading: false };
  },
});

const INITIAL_STATE = {
  loading: false,
  results: undefined,
  totalResults: 0,
  error: false,
} satisfies SearchState;

const DEFAULT_QUERY_RESULT = {
  query: "",
  products: undefined,
  suggestions: undefined,
  totalProductsResult: 0,
};

type UseQueryParams = {
  clearSearch: boolean;
  searchShopType?: string;
};

function useQuery(query: string, params?: UseQueryParams) {
  const { user } = useAuth();
  const { currentShopType } = useShopTypeContext();

  const [state, dispatch] = useReducer(searchReducer, INITIAL_STATE);

  const [searchProvider, isReady] = useSearchProvider();

  useEffect(() => {
    if (!params?.clearSearch) {
      return;
    }

    dispatch({
      type: "SET_RESULT",
      payload: DEFAULT_QUERY_RESULT,
    });
  }, [params?.clearSearch]);

  useEffect(() => {
    if (!query || query.length < 2) {
      dispatch({
        type: "SET_RESULT",
        payload: DEFAULT_QUERY_RESULT,
      });

      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    if (!isReady || !searchProvider) {
      return;
    }

    searchProvider
      .query(query, {
        gender: params?.searchShopType || currentShopType,
        isLoggedIn: Boolean(user?.member_id),
      })
      .then((results) => dispatch({ type: "SET_RESULT", payload: results }))
      .catch((error) => {
        console.error("[Search] An error occurred:", error);
        dispatch({ type: "SET_ERROR", payload: true });
      });
  }, [
    query,
    currentShopType,
    searchProvider,
    isReady,
    user?.member_id,
    params?.searchShopType,
  ]);

  return state;
}

export { useQuery };
